import { handleApi } from "../utils/authentication";
import { store } from "../redux/store";

const JSON_HEADERS = { "Content-Type": "application/json" };

const buildQueryString = (params) => {
  const qs = new URLSearchParams();
  Object.entries(params ?? {}).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    qs.set(k, String(v));
  });
  const s = qs.toString();
  return s ? `?${s}` : "";
};

const safeNumber = (v) => {
  if (v === null || v === undefined) return 0;
  const n = typeof v === "number" ? v : Number(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
};

const safeString = (v) => (v === null || v === undefined ? "" : String(v));

const monthLabel = (yyyyMm) => {
  if (!yyyyMm) return "";
  const [y, m] = String(yyyyMm).split("-");
  const d = new Date(Number(y), Math.max(0, Number(m) - 1), 1);
  return d.toLocaleString("en", { month: "short" });
};

const buildEmptyDoughnut = () => ({
  labels: [],
  datasets: [
    {
      data: [],
      backgroundColor: [],
      borderWidth: 0,
      hoverOffset: 4,
    },
  ],
});

const channelPalette = ["#7F56D9", "#9E77ED", "#C4B5FD", "#EDE9FE"];

const buildChannelBreakdown = (channels = [], metricKey) => {
  const labels = channels.map((c) => safeString(c.channel));
  const values = channels.map((c) => safeNumber(c[metricKey]));
  const imprValues = channels.map((c) => safeNumber(c.impressions));
  const imprPcts = channels.map((c) => safeNumber(c.impression_percentage));

  const allZero = values.length > 0 && values.every((v) => v === 0);
  const hasImprData = imprValues.some((v) => v > 0);

  // When the card's own metric is all-zero, fall back to impressions data
  // for BOTH the donut visual AND the legend values/percentages.
  const usingFallback = allZero && hasImprData;

  const chartValues = usingFallback
    ? imprValues // impressions distribution drives the donut
    : allZero
      ? values.map(() => 1) // truly zero → equal segments
      : values; // real metric data

  const displayValues = usingFallback ? imprValues : values;
  const displayPcts = usingFallback
    ? imprPcts
    : values.map((_, i) =>
        safeNumber(
          channels[i]?.[`${metricKey}_percentage`] ??
            channels[i]?.lead_percentage,
        ),
      );

  const pcts = channels.map(
    (_, i) => `${displayValues[i].toLocaleString()} (${displayPcts[i]}%)`,
  );

  const colors = labels.map(
    (_, i) => channelPalette[i % channelPalette.length],
  );

  return { labels, values: displayValues, chartValues, pcts, colors };
};

const normalizeTrafficRadar = (trafficSources = []) => {
  // Backend returns: [{ day: 'Sunday', 'Social Media': n, 'Search': n, 'Paid Media': n }, ...]
  const dayOrder = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];
  const short = {
    Monday: "Mon",
    Tuesday: "Tue",
    Wednesday: "Wed",
    Thursday: "Thu",
    Friday: "Fri",
    Saturday: "Sat",
    Sunday: "Sun",
  };

  const byDay = new Map(trafficSources.map((d) => [d.day, d]));
  const rows = dayOrder.map((d) => byDay.get(d) ?? { day: d });

  const channels = [
    { key: "Social Media", color: "#EE46BC", fill: "rgba(238,70,188,0.25)" },
    { key: "Search", color: "#7F56D9", fill: "rgba(127,86,217,0.35)" },
    { key: "Paid Media", color: "#06AED4", fill: "rgba(6,174,212,0.25)" },
  ];

  const raw = channels.map((c) => rows.map((r) => safeNumber(r[c.key])));
  const max = Math.max(0, ...raw.flat());
  const scaled =
    max > 0
      ? raw.map((arr) => arr.map((v) => Math.round((v / max) * 100)))
      : raw;

  return {
    labels: dayOrder.map((d) => short[d] ?? d),
    datasets: channels.map((c, idx) => ({
      label: c.key,
      data: scaled[idx],
      backgroundColor: c.fill,
      borderColor: c.color,
      pointBackgroundColor: c.color,
      pointRadius: 3,
      borderWidth: 2,
    })),
  };
};

const normalizeAcquireUsers = (acq) => {
  // Backend: { channels: [...], monthly_breakdown: [], summary: { channelName: { traffic_channel, source, referrals } } }
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const channels = Array.isArray(acq?.channels)
    ? acq.channels
    : ["Social Media", "Search", "Paid Media"];
  const summary =
    acq?.summary && typeof acq.summary === "object" ? acq.summary : {};
  const breakdown = Array.isArray(acq?.monthly_breakdown)
    ? acq.monthly_breakdown
    : [];

  const colors = ["#7F56D9", "#9E77ED", "#C4B5FD"];

  if (breakdown.length > 0) {
    // Expecting something like: [{ month: '2026-01', Social Media: 10, Search: 5, Paid Media: 3 }, ...]
    const labels = breakdown.map(
      (b) => monthLabel(b.month) || safeString(b.month),
    );
    return {
      labels,
      datasets: channels.map((ch, i) => ({
        label: ch,
        data: breakdown.map((b) => safeNumber(b[ch] ?? b[ch.toLowerCase()])),
        backgroundColor: colors[i % colors.length],
        borderRadius: i === channels.length - 1 ? 4 : 0,
        barPercentage: 0.55,
        categoryPercentage: 0.7,
      })),
    };
  }

  // Fallback: show a single stacked bar ("Total") built from summary.
  const totalFor = (channelName) => {
    const row = summary[channelName] ?? summary[channelName.toLowerCase()];
    if (!row) return 0;
    return safeNumber(row.traffic_channel ?? row.sessions ?? row.total ?? 0);
  };

  return {
    labels: ["Total"],
    datasets: channels.map((ch, i) => ({
      label: ch,
      data: [totalFor(ch)],
      backgroundColor: colors[i % colors.length],
      borderRadius: i === channels.length - 1 ? 4 : 0,
      barPercentage: 0.55,
      categoryPercentage: 0.7,
    })),
  };
};

export async function fetchReportsAnalytics(payload) {
  const qs = buildQueryString(payload);
  return await handleApi(`reports/analytics${qs}`, "GET", JSON_HEADERS);
}

export async function fetchSessionsByCountry(payload) {
  const qs = buildQueryString(payload);
  return await handleApi(
    `reports/sessions-by-country${qs}`,
    "GET",
    JSON_HEADERS,
  );
}

export async function fetchUserAcquisitionChannels(payload) {
  const qs = buildQueryString(payload);
  return await handleApi(
    `reports/user-acquisition-channels${qs}`,
    "GET",
    JSON_HEADERS,
  );
}

export function adaptReportsAnalyticsResponse(apiData) {
  const data = apiData?.data ?? apiData;

  const overview = data?.overview ?? {};
  const metricCards = [
    {
      label: overview?.media_spend?.label ?? "Media spend",
      sub: overview?.media_spend?.description ?? "Total marketing spend",
      metric: "Cost",
      value: overview?.media_spend?.currency
        ? `${overview.media_spend.currency} ${safeString(overview?.media_spend?.value)}`
        : `$${safeString(overview?.media_spend?.value ?? "0")}`,
    },
    {
      label: overview?.impressions?.label ?? "Impressions",
      sub: overview?.impressions?.description ?? "Total number of impressions",
      metric: "Impressions",
      value: safeString(overview?.impressions?.value ?? "0"),
    },
    {
      label: overview?.clicks?.label ?? "Clicks",
      sub: overview?.clicks?.description ?? "Clicks for the entire campaign",
      metric: "Clicks",
      value: safeString(overview?.clicks?.value ?? "0"),
    },
    {
      label: overview?.leads?.label ?? "Leads",
      sub: overview?.leads?.description ?? "Total number of leads",
      metric: "Leads",
      value: safeString(overview?.leads?.value ?? "0"),
    },
  ];

  const state = data?.impressions_by_state ?? {};
  const stateRows = Array.isArray(state?.states) ? state.states : [];
  const states = stateRows.map((s) => ({
    state: safeString(s.state),
    value: safeNumber(s.impressions ?? s.value),
    pct: safeNumber(s.percentage ?? s.pct),
  }));
  const tooltipRow = stateRows[0] ?? null;
  const stateTooltip = tooltipRow
    ? {
        label: safeString(tooltipRow.state),
        value: safeNumber(tooltipRow.impressions).toLocaleString(),
      }
    : { label: "Illinois", value: "0" };

  const session = data?.session_analytics ?? {};
  const overviewStats = [
    {
      label: "Users",
      value: safeString(session?.users?.value ?? "0"),
      change: safeString(session?.users?.change ?? ""),
      up: safeString(session?.users?.trend).toLowerCase() !== "down",
    },
    {
      label: "Sessions",
      value: safeString(session?.sessions?.value ?? "0"),
      change: safeString(session?.sessions?.change ?? ""),
      up: safeString(session?.sessions?.trend).toLowerCase() !== "down",
    },
    {
      label: "Bounce rate",
      value: safeString(session?.bounce_rate?.value ?? "0%"),
      change: safeString(session?.bounce_rate?.change ?? ""),
      up:
        safeString(session?.bounce_rate?.trend).toLowerCase() !== "down"
          ? true
          : false,
    },
    {
      label: "Session duration",
      value: safeString(session?.session_duration?.value ?? "0m 0s"),
      change: safeString(session?.session_duration?.change ?? ""),
      up: safeString(session?.session_duration?.trend).toLowerCase() !== "down",
    },
  ];

  const ts = Array.isArray(data?.time_series) ? data.time_series : [];
  const labels = ts.length
    ? ts.map((r) => monthLabel(r.month) || safeString(r.month))
    : [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
  const impressionsSeries = ts.length
    ? ts.map((r) => safeNumber(r.impressions))
    : new Array(labels.length).fill(0);
  const clicksSeries = ts.length
    ? ts.map((r) => safeNumber(r.clicks))
    : new Array(labels.length).fill(0);
  const overviewLineData = {
    labels,
    datasets: [
      {
        label: "Impressions",
        data: impressionsSeries,
        borderColor: "#7F56D9",
        backgroundColor: "rgba(127,86,217,0.08)",
        tension: 0.4,
        fill: true,
        pointRadius: 0,
        borderWidth: 2,
      },
      {
        label: "Clicks",
        data: clicksSeries,
        borderColor: "#9E77ED",
        backgroundColor: "rgba(158,119,237,0.04)",
        tension: 0.4,
        fill: false,
        pointRadius: 0,
        borderWidth: 2,
      },
    ],
  };

  const topChannels = data?.top_channels ?? {};
  const leadsChannels = Array.isArray(topChannels?.leads)
    ? topChannels.leads
    : [];
  const impressionsChannels = Array.isArray(topChannels?.impressions)
    ? topChannels.impressions
    : [];
  const enrollmentsChannels = Array.isArray(topChannels?.enrollments)
    ? topChannels.enrollments
    : [];
  const applicationsChannels = Array.isArray(topChannels?.applications)
    ? topChannels.applications
    : [];

  const trafficRadarData = normalizeTrafficRadar(
    Array.isArray(data?.traffic_sources) ? data.traffic_sources : [],
  );

  return {
    metricCards,
    impressionsByState: {
      totalLabel: safeString(state?.total_impressions ?? "0"),
      states,
      tooltip: stateTooltip,
    },
    overview: {
      stats: overviewStats,
      lineData: overviewLineData,
    },
    channelBreakdowns: {
      leads: buildChannelBreakdown(leadsChannels, "leads"),
      impressions: buildChannelBreakdown(impressionsChannels, "impressions"),
      enrollments: buildChannelBreakdown(enrollmentsChannels, "enrollments"),
      applications: buildChannelBreakdown(applicationsChannels, "applications"),
    },
    trafficRadarData,
  };
}

export function adaptSessionsByCountryResponse(apiData) {
  const data = apiData?.data ?? apiData;
  const total = safeNumber(data?.total_sessions ?? 0);
  const rows = Array.isArray(data?.countries) ? data.countries : [];

  const top = rows.slice(0, 3);
  const other = rows.slice(3);
  const otherTotal = other.reduce((sum, r) => sum + safeNumber(r.sessions), 0);

  const labels = top.map((r) => safeString(r.country));
  const values = top.map((r) => safeNumber(r.sessions));
  if (otherTotal > 0) {
    labels.push("Other");
    values.push(otherTotal);
  }

  const colors = ["#7F56D9", "#C4B5FD", "#EDE9FE", "#533291"];
  return {
    total,
    chartData: {
      labels,
      datasets: [
        {
          data: values,
          backgroundColor: labels.map((_, i) => colors[i % colors.length]),
          borderWidth: 0,
          hoverOffset: 4,
        },
      ],
    },
  };
}

export function adaptUserAcquisitionResponse(apiData) {
  const data = apiData?.data ?? apiData;
  return {
    categoryTabs: ["Traffic channel", "Source", "Referrals"],
    timeTabs: ["12 months", "30 days", "7 days"],
    chartData: normalizeAcquireUsers(data),
  };
}

export function getSafeEmptySessionsByCountry() {
  return { total: 0, chartData: buildEmptyDoughnut() };
}

// ── Download Report PDF ───────────────────────────────────────────────────────
export async function downloadReportPdf(clientId) {
  const { user } = store.getState();
  const headers = {};
  if (user?.token) {
    headers["token"] = user.token;
  }

  const url = `${process.env.REACT_APP_API_URL}reports/download-pdf?client_id=${encodeURIComponent(clientId)}`;
  const response = await fetch(url, { method: "GET", headers });

  if (!response.ok) {
    let message = `Export failed (${response.status})`;
    try {
      const body = await response.json();
      message = body?.message || body?.error || message;
    } catch (_) {}
    throw new Error(message);
  }

  const blob = await response.blob();
  const contentDisposition = response.headers.get("content-disposition") ?? "";
  const match = contentDisposition.match(/filename[^;=\n]*=["']?([^"';\n]+)/i);
  const filename = match?.[1]?.trim() || `report-${clientId}.pdf`;

  return { blob, filename };
}
